import { ThemeEvents, SellingPlanUpdateEvent } from '@theme/events';

/**
 * selling-plan-picker: syncs selling plan selection with URL, dispatches updates with refreshed HTML
 * Expects inputs with name="selling_plan" inside the element.
 */
class SellingPlanPicker extends HTMLElement {
  #controller = new AbortController();
  #sectionTarget;

  connectedCallback() {
    const { signal } = this.#controller;

    this.addEventListener('change', this.#onChange, { signal });

    // Listen for variant updates so we can re-fetch HTML with the current selling plan applied
    this.#sectionTarget = this.closest('.shopify-section, dialog') || document;
    this.#sectionTarget.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });

    // Apply selling_plan from URL if present
    this.#applyUrlSellingPlan();
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  #currentPlanId() {
    const input = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="selling_plan"]:checked'));
    if (!input) return undefined;
    return input.value || null;
  }

  #applyUrlSellingPlan() {
    const params = new URL(window.location.href).searchParams;
    const planFromUrl = params.get('selling_plan');
    if (!planFromUrl) return;

    const match = /** @type {HTMLInputElement | undefined} */ (
      Array.from(this.querySelectorAll('input[name="selling_plan"]')).find((input) => input.value === planFromUrl)
    );
    if (match && !match.checked) {
      match.checked = true;
    }
  }

  #onChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (target.name !== 'selling_plan') return;

    const planId = target.value || null;
    this.#syncUrl(planId);
    this.#dispatchPlanUpdate(planId);
  };

  #onVariantUpdate = () => {
    const currentPlan = this.#currentPlanId();
    // Only refetch if we have a selection (null counts as one-time choice)
    if (currentPlan === undefined) return;
    this.#dispatchPlanUpdate(currentPlan);
  };

  #syncUrl(planId) {
    const url = new URL(window.location.href);
    if (planId) {
      url.searchParams.set('selling_plan', planId);
    } else {
      url.searchParams.delete('selling_plan');
    }
    window.history.replaceState({}, '', url.toString());
  }

  async #dispatchPlanUpdate(planId) {
    const productId = this.closest('[data-product-id]')?.getAttribute('data-product-id') || '';
    const variantId = this.closest('product-form-component')?.querySelector('input[name="id"]')?.value || '';
    const sourceId = this.id || 'selling-plan-picker';

    // Fetch updated section HTML similar to variant picker
    const sectionId = this.closest('.shopify-section')?.id?.replace('shopify-section-', '') || '';
    if (!sectionId) {
      return; // avoid duplicate events without HTML when outside a section (sticky bars, etc.)
    }

    const url = new URL(window.location.href);
    url.searchParams.delete('sections');
    url.searchParams.delete('section_id');
    if (planId) {
      url.searchParams.set('selling_plan', planId);
    } else {
      url.searchParams.delete('selling_plan');
    }
    if (variantId) {
      url.searchParams.set('variant', variantId);
    }
    url.searchParams.set('section_id', sectionId);

    try {
      const response = await fetch(url.toString(), { headers: { Accept: 'text/html' } });
      const text = await response.text();
      const html = new DOMParser().parseFromString(text, 'text/html');
      this.dispatchEvent(new SellingPlanUpdateEvent({ sellingPlanId: planId }, sourceId, { productId, html }));
    } catch (error) {
      console.error('Failed to fetch selling plan HTML', error);
      this.dispatchEvent(new SellingPlanUpdateEvent({ sellingPlanId: planId }, sourceId, { productId }));
    }
  }
}

if (!customElements.get('selling-plan-picker')) {
  customElements.define('selling-plan-picker', SellingPlanPicker);
}
